import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';

import {
  getActiveCycleVersionId,
  getBadgeGating,
  getBadgeStatus,
  getCycleScopedStatus,
  type ReviewBadgeDerivation,
  type ReviewBadgeGatingInput,
  type VersionScopedPanelContext,
  type VersionScopedReviewChange,
} from './reviewBadgeShared';
import {ReviewBadgeStatus} from './ReviewConstants';

/**
 * Cyclical review badge helpers keyed on reviewItemVersionApiIdentifier, which scopes each
 * change to a version so older versions show their own cycle rather than the current one.
 * Linear reviewables use linearReviewBadgeDisplayState instead.
 */

export interface CyclicalVersionBadgeResult {
  kind: 'hidden';
}

export interface CyclicalVersionBadgeStatusResult {
  kind: 'status';
  status: ReviewBadgeStatus;
  scopedStatus: ReviewStatus | null;
}

export type CyclicalVersionBadgeDerivation = CyclicalVersionBadgeResult | CyclicalVersionBadgeStatusResult;

const HIDDEN_REVIEW_BADGE_DERIVATION = {
  isHidden: true,
  badgeStatus: ReviewBadgeStatus.IN_PROGRESS,
  reviewStatus: undefined,
  showSendForReview: false,
  showSummaryPanel: false,
} satisfies ReviewBadgeDerivation;

/** Scopes review summary panel data to the open version for cyclical reviewables. */
export function deriveCyclicalPanelContext<TChange extends VersionScopedReviewChange>({
  openVersionId,
  globalStatus,
  reviewChanges,
}: {
  openVersionId: string;
  globalStatus: ReviewStatus;
  reviewChanges: ReadonlyArray<TChange>;
}): VersionScopedPanelContext<TChange> {
  const scopedReviewChanges = reviewChanges.filter(
    (change) => change.reviewItemVersionApiIdentifier === openVersionId
  );
  const scopedStatus = getCycleScopedStatus(reviewChanges, openVersionId);
  const activeCycleVersionId = getActiveCycleVersionId(reviewChanges);
  const isActiveCycleVersion = activeCycleVersionId != null && openVersionId === activeCycleVersionId;

  return {
    scopedReviewChanges,
    scopedStatus,
    isActiveCycleVersion,
    panelStatus: scopedStatus ?? globalStatus,
  };
}

/**
 * Derives the cyclical review badge for the open template version.
 *
 * @param reviewChanges Time-ordered array of all review changes on the review (ascending by createdAt).
 */
function getCyclicalVersionBadge({
  openVersionId,
  isCurrentEditableDraft,
  parentReviewLifecycleCount,
  reviewChanges,
}: {
  openVersionId: string;
  isCurrentEditableDraft: boolean;
  parentReviewLifecycleCount: number;
  reviewChanges: ReadonlyArray<VersionScopedReviewChange>;
}): CyclicalVersionBadgeDerivation {
  const scopedStatus = getCycleScopedStatus(reviewChanges, openVersionId);

  if (scopedStatus != null) {
    return {kind: 'status', status: getBadgeStatus(scopedStatus), scopedStatus};
  }
  if (isCurrentEditableDraft && parentReviewLifecycleCount > 0) {
    return {kind: 'status', status: ReviewBadgeStatus.IN_PROGRESS, scopedStatus: null};
  }
  return {kind: 'hidden'};
}

/** Assembles cyclical review badge status, review status, and dropdown gating for the controller. */
export function deriveCyclicalReviewBadge(
  input: ReviewBadgeGatingInput & {
    openVersionId: string;
    isCurrentEditableDraft: boolean;
    reviewChanges: ReadonlyArray<VersionScopedReviewChange>;
  }
): ReviewBadgeDerivation {
  const versionBadge = getCyclicalVersionBadge({
    openVersionId: input.openVersionId,
    isCurrentEditableDraft: input.isCurrentEditableDraft,
    parentReviewLifecycleCount: input.parentReviewLifecycleCount,
    reviewChanges: input.reviewChanges,
  });
  if (versionBadge.kind === 'hidden') {
    return HIDDEN_REVIEW_BADGE_DERIVATION;
  }
  return {
    isHidden: false,
    badgeStatus: versionBadge.status,
    reviewStatus: versionBadge.scopedStatus ?? undefined,
    ...getCyclicalBadgeGating({
      enableReviewLifecycle: input.enableReviewLifecycle,
      canSendForReview: input.canSendForReview,
      parentReviewLifecycleCount: input.parentReviewLifecycleCount,
      reviewUsesReviewLifecycle: input.reviewUsesReviewLifecycle,
      scopedStatus: versionBadge.scopedStatus,
      isCurrentEditableDraft: input.isCurrentEditableDraft,
    }),
  };
}

/**
 * Badge gating for cyclical reviewables. Uses scopedStatus (not the lagging global review.status scalar)
 * so a draft with no cycle yet can offer send-for-review while review.status sits at CYCLE_COMPLETED.
 */
function getCyclicalBadgeGating({
  enableReviewLifecycle,
  canSendForReview,
  parentReviewLifecycleCount,
  reviewUsesReviewLifecycle,
  scopedStatus,
  isCurrentEditableDraft,
}: ReviewBadgeGatingInput & {
  scopedStatus: ReviewStatus | null;
  isCurrentEditableDraft: boolean;
}): {showSendForReview: boolean; showSummaryPanel: boolean} {
  if (!isCurrentEditableDraft) {
    return {
      showSendForReview: false,
      showSummaryPanel: reviewUsesReviewLifecycle || scopedStatus != null,
    };
  }
  return getBadgeGating({
    enableReviewLifecycle,
    canSendForReview,
    parentReviewLifecycleCount,
    reviewStatus: scopedStatus,
    reviewUsesReviewLifecycle,
  });
}

export const _forTesting = {
  getCyclicalVersionBadge,
  getCyclicalBadgeGating,
  HIDDEN_REVIEW_BADGE_DERIVATION,
};
